import React, { useEffect } from 'react'
import { Box, Paper } from '@mui/material'
import { logoLight, logoNoBackground, opticTruckBlue } from 'assets'
import { Outlet, useLocation, useNavigate } from 'react-router-dom'
import { useAuth } from 'contexts/AuthContext'
import { useMFA } from 'hooks/auth/useMFA'
import { AuthBootSplash } from 'components/auth/AuthBootSplash'
import { AuthBrandHero } from 'components/auth/AuthBrandHero'
import { AuthRoadBackdrop } from 'components/auth/AuthRoadBackdrop'
import { readAuthSessionHint } from 'components/auth/authSessionHint'
import AuthFooter from './AuthFooter'
import { SAFE_AREA_BOTTOM, SAFE_AREA_TOP } from 'core/theme.tokens'

const AuthLayout: React.FC = () => {
  const { isLoading, currentUser, userInfo } = useAuth()
  const { resolver } = useMFA()
  const navigate = useNavigate()
  const location = useLocation()

  useEffect(() => {
    // Require currentUser (React state) so a leftover userInfo.role after
    // multi-tab logout cannot bounce into protected routes / logo splash.
    if (userInfo?.role && currentUser) {
      if (userInfo?.role === 'babyAdmin') {
        navigate(`/admin`)
      } else {
        navigate(`/${userInfo?.role}`)
      }
    }
    if (resolver && location.pathname !== '/login-mfa-code') {
      navigate('/login-mfa-code')
    }
    if (location.pathname === '/' && !currentUser && !isLoading) {
      navigate('/login')
    }
  }, [resolver, location.pathname, navigate, currentUser, userInfo, isLoading])

  if (isLoading && readAuthSessionHint()) {
    return <AuthBootSplash logoSrc={logoLight} />
  }

  return (
    <Box
      sx={{
        position: 'relative',
        minHeight: '100dvh',
        display: 'flex',
        flexDirection: 'column',
        bgcolor: 'transparent',
        pt: SAFE_AREA_TOP,
        pb: SAFE_AREA_BOTTOM,
      }}
    >
      <AuthRoadBackdrop />
      <Box
        sx={{
          position: 'relative',
          zIndex: 1,
          flex: 1,
          display: 'flex',
          alignItems: { xs: 'flex-start', sm: 'center' },
          justifyContent: 'center',
          px: 2,
          py: { xs: 3, sm: 4 },
          overflowY: 'visible',
        }}
      >
        <Box sx={{ width: '100%', maxWidth: 420 }}>
          <AuthBrandHero
            logoSrc={logoNoBackground}
            truckSrc={opticTruckBlue}
          />
          <Paper
            elevation={0}
            sx={{
              px: { xs: 2.5, sm: 3 },
              py: { xs: 2.5, sm: 3 },
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              bgcolor: 'background.paper',
              width: '100%',
              borderRadius: 3,
              border: '1px solid',
              borderColor: 'divider',
              boxShadow: theme => `0 8px 24px ${theme.palette.primary.main}14`,
            }}
          >
            <Outlet />
          </Paper>
        </Box>
      </Box>
      <AuthFooter />
    </Box>
  )
}

export default AuthLayout
